
    import dayjs from 'dayjs';
    import { mapActions, mapMutations, mapGetters, mapState } from 'vuex';
    import SearchPageCompany from '@/components/SearchPageTemplates/SearchPageCompany';
    import SearchDataLayerEventMixin from '@/components/Search/SearchDataLayerEventMixin';

    export default {
        layout: 'discovery',

        components: {
            SearchPageCompany,
        },

        mixins: [SearchDataLayerEventMixin],

        async asyncData({
            store, route,
        }) {
            let brand = {};
            await store.dispatch('brand/getBrandBySlug', route.params.brand).then((response) => {
                brand = response.data;
                store.commit('search/SET', { key: 'brand', value: response.data });
            });

            const pageMetaData = {
                meta_title: `${brand.name} Jobs`,
                meta_description: `Latest ${brand.name} jobs. Over ${brand.live_job_count} jobs live with new jobs added daily. Don't miss out, sign up and get matched today!`,
                canonical_url: route.path,
            };
            store.commit('search/SET', { key: 'pageMetaData', value: pageMetaData });

            return {
                pageMetaData,
                brand,
            };
        },

        data() {
            return {
                loading: true,
            };
        },

        computed: {
            ...mapGetters('jobModal', {
                $_isJobModalOpen: 'isJobModalOpen',
            }),
            ...mapState({
                $_me: state => state.me.user,
            }),

            getCanonicalUrl() {
                const siteUrl = this.$siteUrl;
                const canonicalUrl = this.pageMetaData.canonical_url.replace('/', '');
                return siteUrl + canonicalUrl;
            },
        },

        async mounted() {
            await this.getSearchPayload();
            await this.getBrandArticles();
            this.getSearchFilterAttributes();
            this.$store.commit('search/SET', { key: 'activeSearchFilter', value: false });
            this.updateMe();
        },

        methods: {
            ...mapActions('job', {
                $_searchJobs: 'searchJobs',
            }),

            ...mapActions('me', {
                $_syncScoring: 'syncScoring',
            }),

            ...mapActions('brand', {
                $_getBrandArticles: 'getBrandArticles',
                $_getBrandEeoTags: 'getBrandEeoTags',
            }),

            ...mapMutations('search', {
                $_SET: 'SET',
                $_SET_SEARCH_OBJECT: 'SET_OBJECT',
            }),

            ...mapActions('attribute', {
                $_getSearchFilterAttributes: 'getSearchFilterAttributes',
            }),

            async getSearchFilterAttributes() {
                let searchFilterAttributes = this.$_search.searchFilterAttributes;
                if (searchFilterAttributes.length <= 0) {
                    searchFilterAttributes = await this.$_getSearchFilterAttributes();
                    this.$_SET({
                        key: 'searchFilterAttributes',
                        value: searchFilterAttributes,
                    });
                }
            },

            async getSearchPayload() {
                this.loading = true;
                let url = '';
                if (this.isHomePage) {
                    url = this.$jobSearchDefaultUrlQuery;
                } else {
                    url = this.$route.fullPath;
                }
                const allowBackFill = url.search('/br_') <= 0;
                const searchPayload = await this.$_searchJobs({
                    url,
                    locale: 'AU',
                    allow_backfill: allowBackFill,
                }).then().catch((e) => {
                    if (e.name === 'TypeError [ERR_UNESCAPED_CHARACTERS]' || e.response.status === 404 || e.response.status === 422) {
                        const url = `${this.$_currentAppUrl}/not-found`;
                        this.$route.redirect(url);
                    }
                    return true;
                });

                this.$_SET_SEARCH_OBJECT({
                    key: 'searchPayload',
                    value: searchPayload,
                });

                this.$_getBrandEeoTags().then((response) => {
                    this.$_SET({
                        key: 'availableEeoTags',
                        value: response.data,
                    });
                });

                this.jobSearchDataLayerPush();
                this.jobSearchEventPush();
            },

            async getBrandArticles() {
                let brandArticles = [];
                brandArticles = await this.$_getBrandArticles(this.brand.id).then(response => response.data).finally(() => {
                    this.loading = false;
                });

                this.$_SET({
                    key: 'brandArticles',
                    value: brandArticles,
                });
            },

            async updateMe() {
                if (this.$_me.id) {
                    await this.$_syncScoring();
                }
            },
        },

        head() {
            return {
                title: `${this.pageMetaData.meta_title || 'Jobs'} - ${dayjs().format('MMM YYYY')} | ${this.$siteName}`,
                meta: [
                    { hid: 'og:title', property: 'og:title', content: this.pageMetaData.meta_title.replace('CareerOne', this.$siteName) },
                    { hid: 'description', name: 'description', content: this.pageMetaData.meta_description.replace('CareerOne', this.$siteName) },
                    { hid: 'og:description', property: 'og:description', content: this.pageMetaData.meta_description.replace('CareerOne', this.$siteName) },
                    { hid: 'robots', name: 'robots', content: 'true' },
                ],
                link: [
                    { hid: 'canonical', rel: 'canonical', href: this.getCanonicalUrl },
                ],
                script: [
                    {
                        type: 'application/ld+json',
                        innerHTML: '{\n' +
                            '  "@context": "http://schema.org",\n' +
                            '  "@type": "WebPage",\n' +
                            '  "mainEntityOfPage": {\n' +
                            '    "@type": "SearchResultsPage",\n' +
                            `    "name": "${this.pageMetaData.meta_title || 'CareerOne'}",\n` +
                            `    "description": "${this.pageMetaData.meta_description || ''}",\n` +
                            `    "lastReviewed": "${(new Date((new Date()).getFullYear(), (new Date()).getMonth(), 1)).toISOString()}"\n` +
                            '  }\n' +
                            '}',
                    },
                ],
                bodyAttrs: {
                    class: [
                        'bg-c1-light-400',
                        this.$_isJobModalOpen ? 'overflow-hidden' : '',
                        this.$_search.isSearchFullscreenMode ? 'body--with-fullscreen-component' : '',
                    ],
                },
            };
        },
    };
